import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, MapPin, Lock } from "lucide-react";
import { Overline } from "../Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scopes = ["Pentesting", "SOC 24/7", "Ciberinteligencia", "Red Team", "Auditoría integral"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", scope: "", message: "" });
  const [scope, setScope] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Completa nombre, email y mensaje.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, scope });
      toast.success("Mensaje enviado. Te contactaré en menos de 24h.");
      setForm({ name: "", email: "", company: "", scope: "", message: "" });
      setScope("");
    } catch {
      toast.error("No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3.5 font-sans text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-cyber-cyan focus:ring-1 focus:ring-cyber-cyan";

  return (
    <section
      id="contacto"
      className="relative mx-auto max-w-7xl px-5 py-28 md:px-10 md:py-40"
      data-testid="contact"
    >
      <div className="glass glow-cyan overflow-hidden rounded-3xl">
        <div className="grid lg:grid-cols-[0.85fr_1fr]">
          {/* Left info */}
          <div className="relative flex flex-col justify-between border-b border-white/10 p-10 lg:border-b-0 lg:border-r lg:p-14">
            <div className="animate-floaty absolute -right-20 -top-20 h-64 w-64 rounded-full bg-cyber-violet/25 blur-[100px]" />
            <div className="relative">
              <Overline>Contacto Seguro</Overline>
              <h2 className="mt-6 font-head text-4xl font-bold tracking-tight text-white md:text-5xl">
                Blindemos tu empresa.
              </h2>
              <p className="mt-5 max-w-sm font-sans text-white/60">
                Cuéntame tu reto. Recibirás una evaluación inicial y una propuesta de defensa a medida.
              </p>
            </div>
            <div className="relative mt-12 space-y-4 font-mono-tech text-xs uppercase tracking-[0.15em] text-white/60">
              <p className="flex items-center gap-3"><Mail className="h-4 w-4 text-cyber-cyan" /> contacto@cyvrlabs.com</p>
              <p className="flex items-center gap-3"><MapPin className="h-4 w-4 text-cyber-cyan" /> Operaciones remotas · Global</p>
              <p className="flex items-center gap-3"><Lock className="h-4 w-4 text-cyber-cyan" /> Comunicación cifrada extremo a extremo</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="p-10 lg:p-14" data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="contact-name" className={input} placeholder="Nombre" value={form.name} onChange={set("name")} />
              <input data-testid="contact-email" type="email" className={input} placeholder="Email" value={form.email} onChange={set("email")} />
            </div>
            <input data-testid="contact-company" className={`${input} mt-4`} placeholder="Empresa (opcional)" value={form.company} onChange={set("company")} />

            <div className="mt-6">
              <p className="mb-3 font-mono-tech text-[11px] uppercase tracking-[0.15em] text-white/40">Servicio de interés</p>
              <div className="flex flex-wrap gap-2">
                {scopes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`scope-${s}`}
                    onClick={() => setScope(s)}
                    className={`rounded-full border px-4 py-2 font-mono-tech text-[11px] uppercase tracking-wider transition-colors duration-300 ${
                      scope === s
                        ? "border-cyber-cyan bg-cyber-cyan/10 text-cyber-cyan"
                        : "border-white/10 text-white/50 hover:border-white/30"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              data-testid="contact-message"
              className={`${input} mt-6 min-h-[120px] resize-none`}
              placeholder="Describe tu reto de ciberseguridad..."
              value={form.message}
              onChange={set("message")}
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyber-cyan px-8 py-4 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Enviando</>
              ) : (
                <>Enviar solicitud <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
